import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/**
 * How close the attorney may get to a drawing at full size, and how they get there.
 *
 * Zoom is a size rather than a coordinate system: everything on the drawing is a fraction
 * of the box it sits over, so growing the box moves every mark with it.
 */

/** The fitted size, which is where a drawing opens and what every exit path returns it to. */
export const ZOOM_FIT = 1;

/**
 * Half the fitted size, as far back as the attorney can stand. The drawing takes the width
 * of its window, so a tall sheet runs past the bottom at the fit; standing back trades
 * detail for how much of it is in view at once. Further than half and a patent drawing's
 * thinnest lines stop being lines.
 */
export const ZOOM_MIN = 0.5;

/** Where a drawing opens, and what every exit path returns it to: as far back as it goes,
    so the first numeral is placed against the sheet rather than against a part of it. */
export const ZOOM_START = ZOOM_MIN;

/**
 * Twice the fitted size, and no further. The bound is the raster's rather than the
 * layout's: uploads arrive at whatever resolution the attorney's file happens to be, so
 * past this the drawing gains room without gaining detail.
 */
export const ZOOM_MAX = 2;

/** A quarter per press: two back from the fit, four forward. */
export const ZOOM_STEP = 0.25;

/**
 * The level at which a drawing's whole height is in the window: below the fit for a sheet
 * taller than the window it is fitted across, the fit itself for any other.
 */
export function wholeLevel(
  room: { width: number; height: number },
  aspect: number,
): number {
  const fittedHeight = room.width / aspect;
  return fittedHeight <= 0
    ? ZOOM_FIT
    : Math.min(ZOOM_FIT, room.height / fittedHeight);
}

/**
 * The next level up or down, kept inside the bounds and on a whole quarter. `floor` is how
 * far back this drawing may stand: half the fit, or less for a sheet that is only whole below
 * it. Below half the fit there is no quarter step, only the whole figure, and the first step
 * forward from it is half the fit.
 */
export function stepped(
  level: number,
  by: number,
  floor: number = ZOOM_MIN,
): number {
  if (by > 0 && level < ZOOM_MIN) return ZOOM_MIN;
  const next = Math.round((level + by) * 100) / 100;
  // Below half the fit there is one place to stand: the whole figure.
  if (next < ZOOM_MIN) return floor;
  return Math.min(ZOOM_MAX, Math.max(floor, next));
}

/** The level as the readout says it. */
export function percent(level: number): string {
  return `${String(Math.round(level * 100))}%`;
}

/** The part of a scroll window's geometry that says what it is looking at. */
export interface ScrollWindow {
  scrollLeft: number;
  scrollTop: number;
  clientWidth: number;
  clientHeight: number;
  scrollWidth: number;
  scrollHeight: number;
}

/** A point on the whole drawing, as fractions of it. */
export interface Centre {
  x: number;
  y: number;
}

/**
 * What sits in the middle of the window, as a fraction of the whole drawing. A window with
 * no measured size has not been laid out, and the middle of it is the middle of the window.
 */
export function centreOf(view: ScrollWindow): Centre {
  return {
    x:
      view.scrollWidth === 0
        ? 0.5
        : (view.scrollLeft + view.clientWidth / 2) / view.scrollWidth,
    y:
      view.scrollHeight === 0
        ? 0.5
        : (view.scrollTop + view.clientHeight / 2) / view.scrollHeight,
  };
}

const within = (value: number, room: number) =>
  Math.min(Math.max(0, room), Math.max(0, value));

/**
 * The offsets that put a point back in the middle of a window that has changed size.
 *
 * Clamped here rather than left to the browser, which clamps silently: the number this
 * returns is then the number the window ends up on, and the caller can read it back.
 */
export function scrollKeeping(
  centre: Centre,
  view: ScrollWindow,
): { left: number; top: number } {
  return {
    left: within(
      centre.x * view.scrollWidth - view.clientWidth / 2,
      view.scrollWidth - view.clientWidth,
    ),
    top: within(
      centre.y * view.scrollHeight - view.clientHeight / 2,
      view.scrollHeight - view.clientHeight,
    ),
  };
}

/**
 * Keeping the middle of the window on what was in the middle of it.
 *
 * The centre is taken as the attorney scrolls, so what is read when the size changes is
 * what was on screen a moment before rather than whatever the last render happened to see.
 * Run before the browser paints, so the drawing is never seen at its new size from the old
 * offsets.
 *
 * Returns the scroll handler the window wears, because the two halves are one idea: what
 * the window was looking at, and putting it back.
 */
export function useHoldsTheCentre(
  view: RefObject<HTMLElement | null>,
  zoom: number,
) {
  const centreRef = useRef({ x: 0.5, y: 0.5 });
  const zoomRef = useRef(zoom);

  useLayoutEffect(() => {
    if (zoomRef.current === zoom) return;
    zoomRef.current = zoom;
    const window = view.current;
    if (window === null) return;
    const to = scrollKeeping(centreRef.current, window);
    window.scrollLeft = to.left;
    window.scrollTop = to.top;
    centreRef.current = centreOf(window);
  }, [view, zoom]);

  return () => {
    const window = view.current;
    if (window !== null) centreRef.current = centreOf(window);
  };
}

/** How close the drawing is, how far back it may stand, and the ways that changes. */
export interface ZoomControl {
  level: number;
  floor: number;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Told the level at which this drawing is in view whole, once it has been measured. */
  fitWhole: (whole: number) => void;
}

/**
 * How close the drawing is being looked at, for as long as it is filling the screen.
 *
 * View state only: nothing is written down, and the level is dropped the moment full screen
 * is left, so every way in opens the same way however the last one was left. Moving to
 * another drawing takes it along, because the surface holding it is keyed by the drawing.
 */
export function useFigureZoom(active: boolean): ZoomControl {
  // The level is held against the state that earns it, and dropped in the same render the
  // drawing leaves full screen. Adjusted here rather than in an effect, so there is never
  // a render in which the drawing is back at panel size still carrying a level.
  const [held, setHeld] = useState({
    level: ZOOM_START,
    floor: ZOOM_MIN,
    moved: false,
    active,
  });
  if (held.active !== active) {
    setHeld({ level: ZOOM_START, floor: ZOOM_MIN, moved: false, active });
  }

  return {
    level: held.level,
    floor: held.floor,
    zoomIn: useCallback(() => {
      setHeld((was) => ({
        ...was,
        moved: true,
        level: stepped(was.level, ZOOM_STEP, was.floor),
      }));
    }, []),
    zoomOut: useCallback(() => {
      setHeld((was) => ({
        ...was,
        moved: true,
        level: stepped(was.level, -ZOOM_STEP, was.floor),
      }));
    }, []),
    // A drawing opens as far back as it may stand, so a tall sheet opens whole; one the
    // attorney has already zoomed keeps where they put it.
    fitWhole: useCallback((whole: number) => {
      setHeld((was) => {
        const floor = Math.min(ZOOM_MIN, whole);
        if (floor === was.floor) return was;
        return {
          ...was,
          floor,
          level: was.moved ? Math.max(floor, was.level) : floor,
        };
      });
    }, []),
  };
}
